import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    Chip,
    TextField,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    CircularProgress,

} from '@mui/material';
import {
    Search,
    ExpandMore,
    ErrorOutline,
    WarningAmber,
    CheckCircle,
    Refresh,
    ContentCopy,
    Inventory2,
    Close,
    ImageNotSupported,
    Flag,
    CloudSync,
    ArrowBack,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { WarehouseAPI } from '../../services/api';
import { useToast } from '../common/useToast';
import TablePaginationBar from '../common/TablePaginationBar';
import { tableContainerSx, tableHeadRowSx, tableBodySx } from '../common/tableStyles';
import { DataHealthReport, DataHealthResult, DataHealthIssue, DataHealthSeverity } from '../../types';

const FIELD_LABELS: Record<string, string> = {
    brand: 'Brand',
    category: 'Category',
    subCategory: 'Sub-Category',
    categoryCode: 'Category Code',
    maxRetailPrice: 'MRP',
    basePrice: 'Base Price',
};

const SYSTEM_LABELS: Record<string, string> = { plytix: 'Plytix', unicommerce: 'Unicommerce', tool: 'This tool' };
const SYSTEM_COLORS: Record<string, string> = { plytix: '#16a34a', unicommerce: '#B8451E' };

// One entry per distinct check — this IS the "6 checks" the tool runs, plus the
// existence checks. Drives the Issue Breakdown grid, which is now the primary
// way into the data: pick a problem type, then see exactly the SKUs affected by
// THAT problem, instead of scrolling a wall of near-identical rows.
const CHECK_META: Record<string, { label: string; hint: string }> = {
    name_equals_parent_sku: { label: 'Name = Parent SKU', hint: "Bundle's display name is literally its own Parent SKU" },
    missing_in_plytix: { label: 'Missing in Plytix', hint: 'SKU not found in Plytix at all' },
    missing_in_unicommerce: { label: 'Missing in Unicommerce', hint: 'Never synced to Unicommerce' },
    sale_type_empty: { label: 'Sale Type Missing', hint: 'Empty in the tool, but the SKU is real in Unicommerce' },
    uc_bundle_price_manual: { label: 'UC Bundle Price — Set Manually', hint: 'Bundle price differs in Unicommerce; its API ignores price edits on bundles' },
    plytix_price_missing: { label: 'Price Missing in Plytix', hint: 'MRP and/or Base Price is empty in Plytix (the source of truth)' },
    no_primary_variant: { label: 'No Primary Variant', hint: 'Parent is live but none of its versions is marked Primary' },
    brand: { label: 'Brand', hint: 'vs Plytix vendor and Unicommerce brand' },
    category: { label: 'Category', hint: 'vs Plytix category (top level)' },
    subCategory: { label: 'Sub-Category', hint: 'vs Plytix category (second level)' },
    categoryCode: { label: 'Category Code', hint: 'vs Unicommerce category code' },
    maxRetailPrice: { label: 'MRP', hint: 'vs Plytix and Unicommerce price' },
    basePrice: { label: 'Base Price', hint: 'vs Plytix and Unicommerce base price' },
};

function checkKeyOf(issue: DataHealthIssue): string {
    return issue.type === 'mismatch' ? (issue.field || 'unknown') : issue.type;
}

function isImageUrl(v: string | number | null): v is string {
    return typeof v === 'string' && v.startsWith('http');
}

function formatValue(v: string | number | null): string {
    if (v === null || v === undefined) return 'Empty';
    if (typeof v === 'number') return String(v);
    return v;
}

// Groups a bundle's *field* mismatches (brand/category/pricing/etc.) so "Brand
// differs in Plytix AND Unicommerce" reads as one card instead of two rows that
// both repeat the same "in tool" value.
function groupByField(issues: DataHealthIssue[]) {
    const map = new Map<string, { field: string; items: DataHealthIssue[]; toolValue: string | number | null; severity: DataHealthSeverity }>();
    for (const issue of issues) {
        if (issue.type !== 'mismatch') continue;
        const key = issue.field || 'unknown';
        if (!map.has(key)) {
            map.set(key, { field: key, items: [], toolValue: issue.tool, severity: issue.severity });
        }
        const g = map.get(key)!;
        g.items.push(issue);
        if (issue.severity === 'error') g.severity = 'error';
    }
    return Array.from(map.values()).sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'error' ? -1 : 1));
}

/* ── Stat tile — doubles as a filter toggle ── */
const StatTile: React.FC<{
    icon: React.ReactNode;
    label: string;
    value: number;
    color: string;
    bg: string;
    active: boolean;
    onClick: () => void;
    index: number;
}> = ({ icon, label, value, color, bg, active, onClick, index }) => (
    <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
        whileHover={{ y: -3 }}
        whileTap={{ scale: 0.98 }}
        style={{ flex: '1 1 180px' }}
    >
        <Box
            onClick={onClick}
            sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 2,
                py: 1.5,
                borderRadius: '14px',
                border: '1px solid',
                borderColor: active ? color : '#ECECE4',
                bgcolor: '#ffffff',
                cursor: 'pointer',
                overflow: 'hidden',
                boxShadow: active ? `0 4px 16px ${color}26` : '0 1px 3px rgba(26, 20, 15, 0.04)',
                transition: 'border-color 150ms ease, box-shadow 150ms ease',
                '&:hover': { borderColor: color, boxShadow: `0 8px 20px ${color}20` },
            }}
        >
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: color, opacity: active ? 1 : 0, transition: 'opacity 150ms ease' }} />
            <Box sx={{ width: 38, height: 38, borderRadius: '10px', bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color, flexShrink: 0 }}>
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: '#1A1410', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600 }}>{label}</Typography>
            </Box>
        </Box>
    </motion.div>
);

/* ── Issue Breakdown card — the primary navigation: pick a problem, see its SKUs ── */
const CheckCard: React.FC<{
    checkKey: string;
    total: number;
    plytixCount: number;
    ucCount: number;
    severity: DataHealthSeverity;
    active: boolean;
    onClick: () => void;
    index: number;
}> = ({ checkKey, total, plytixCount, ucCount, severity, active, onClick, index }) => {
    const meta = CHECK_META[checkKey] || { label: checkKey, hint: '' };
    const flagged = checkKey === 'uc_bundle_price_manual';
    const color = severity === 'error' ? '#dc2626' : '#b45309';
    const bg = severity === 'error' ? 'rgba(239, 68, 68, 0.08)' : 'rgba(245, 158, 11, 0.1)';
    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: Math.min(index, 10) * 0.03 }}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
            style={{ flex: '1 1 200px', maxWidth: 260 }}
        >
            <Box
                onClick={onClick}
                sx={{
                    display: 'flex', flexDirection: 'column', gap: 0.5, px: 1.75, py: 1.5,
                    borderRadius: '12px', cursor: 'pointer', height: '100%',
                    border: '1.5px solid', borderColor: active || flagged ? color : '#ECECE4',
                    bgcolor: active || flagged ? bg : '#fff',
                    transition: 'border-color 150ms ease, background-color 150ms ease',
                    '&:hover': { borderColor: color },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1A1410', display: 'flex', alignItems: 'center', gap: 0.5 }}>{flagged && <Flag sx={{ fontSize: 14, color }} />}{meta.label}</Typography>
                    <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{total}</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', lineHeight: 1.3 }}>{meta.hint}</Typography>
                {(plytixCount > 0 || ucCount > 0) && (
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25 }}>
                        {plytixCount > 0 && <Chip size="small" label={`Plytix ${plytixCount}`} sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: `${SYSTEM_COLORS.plytix}1a`, color: SYSTEM_COLORS.plytix }} />}
                        {ucCount > 0 && <Chip size="small" label={`UC ${ucCount}`} sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: `${SYSTEM_COLORS.unicommerce}1a`, color: SYSTEM_COLORS.unicommerce }} />}
                    </Box>
                )}
            </Box>
        </motion.div>
    );
};

/* ── A value with a copy button ── */
const ValueChip: React.FC<{ value: string | number | null; color: string; onCopy: () => void }> = ({ value, color, onCopy }) => {
    if (value === null || value === undefined) {
        return <Typography sx={{ fontSize: '0.78rem', color: 'text.disabled', fontStyle: 'italic' }}>Empty</Typography>;
    }
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            {isImageUrl(value) ? (
                <Box component="img" src={value} alt="" sx={{ width: 30, height: 30, borderRadius: '6px', objectFit: 'cover', border: '1px solid #ECECE4', flexShrink: 0 }} />
            ) : null}
            <Typography sx={{ fontSize: '0.78rem', color, fontWeight: 600, wordBreak: 'break-word', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {formatValue(value)}
            </Typography>
            <Tooltip title="Copy value">
                <IconButton
                    size="small"
                    onClick={() => { navigator.clipboard.writeText(String(value)).catch(() => {}); onCopy(); }}
                    sx={{ p: 0.25, color: 'text.disabled', flexShrink: 0, '&:hover': { color } }}
                >
                    <ContentCopy sx={{ fontSize: 13 }} />
                </IconButton>
            </Tooltip>
        </Box>
    );
};

/* ── Flat, single-purpose table for exactly one selected check — no accordions,
     no repeated chips, just SKU + the values that matter for THIS check ── */
const FlatCheckTable: React.FC<{ checkKey: string; results: DataHealthResult[]; onCopy: () => void }> = ({ checkKey, results, onCopy }) => {
    const isSpecial = ['name_equals_parent_sku', 'missing_in_plytix', 'missing_in_unicommerce', 'sale_type_empty', 'no_primary_variant', 'plytix_price_missing', 'uc_bundle_price_manual'].includes(checkKey);

    if (isSpecial) {
        return (
            <TableContainer component={Paper} variant="outlined" sx={tableContainerSx}>
                <Table size="small" sx={tableBodySx}>
                    <TableHead>
                        <TableRow sx={tableHeadRowSx}>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem' }}>SKU</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem' }}>TYPE</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem' }}>DETAIL</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {results.map(r => {
                            const issue = r.issues.find(i => i.type === checkKey);
                            return (
                                <TableRow key={r.bundleSku} hover>
                                    <TableCell>
                                        <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '0.84rem', lineHeight: 1.25 }}>{r.bundleSku}</Typography>
                                        {r.parentSKU !== r.bundleSku && <Typography sx={{ fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: 600, color: '#6B625B' }}>{r.parentSKU}</Typography>}
                                    </TableCell>
                                    <TableCell><Chip size="small" variant="outlined" color={r.saleType === 'Simple' ? 'primary' : 'default'} label={r.saleType || 'None'} sx={{ height: 20, fontSize: '0.66rem', fontWeight: 600 }} /></TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            {checkKey === 'no_primary_variant'
                                                ? <WarningAmber sx={{ fontSize: 15, color: '#b45309' }} />
                                                : <ErrorOutline sx={{ fontSize: 15, color: '#dc2626' }} />}
                                            <Typography sx={{ fontSize: '0.78rem' }}>
                                                {checkKey === 'name_equals_parent_sku' && `Name is "${issue?.tool}" — same as Parent SKU`}
                                                {checkKey === 'missing_in_plytix' && 'Not found in Plytix'}
                                                {checkKey === 'missing_in_unicommerce' && 'Never synced to Unicommerce'}
                                                {checkKey === 'sale_type_empty' && 'Empty in tool, but item exists in Unicommerce'}
                                                {checkKey === 'uc_bundle_price_manual' && `Tool ${issue?.tool} · Unicommerce ${issue?.other} — update in Unicommerce`}
                                                {checkKey === 'plytix_price_missing' && (() => {
                                                    const f = r.issues.filter(i => i.type === 'plytix_price_missing');
                                                    const names = f.map(i => (i.field === 'maxRetailPrice' ? 'MRP' : 'Base Price')).join(' and ');
                                                    const vals = f.map(i => `${i.field === 'maxRetailPrice' ? 'MRP' : 'Base'} ${i.tool ?? 'empty'}`).join(', ');
                                                    return `${names} empty in Plytix · tool has ${vals}`;
                                                })()}
                                                {checkKey === 'no_primary_variant' && `${issue?.other ?? 'None marked Primary'}${r.saleTag ? ` · Sale Tag ${r.saleTag}` : ''}`}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    const showPlytix = results.some(r => r.issues.some(i => i.field === checkKey && i.system === 'plytix'));
    const showUC = results.some(r => r.issues.some(i => i.field === checkKey && i.system === 'unicommerce'));

    return (
        <TableContainer component={Paper} variant="outlined" sx={tableContainerSx}>
            <Table size="small" sx={tableBodySx}>
                <TableHead>
                    <TableRow sx={tableHeadRowSx}>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem' }}>SKU</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem' }}>TYPE</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem' }}>IN TOOL</TableCell>
                        {showPlytix && <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: SYSTEM_COLORS.plytix }}>PLYTIX</TableCell>}
                        {showUC && <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: SYSTEM_COLORS.unicommerce }}>UNICOMMERCE</TableCell>}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {results.map(r => {
                        const plytixIssue = r.issues.find(i => i.field === checkKey && i.system === 'plytix');
                        const ucIssue = r.issues.find(i => i.field === checkKey && i.system === 'unicommerce');
                        const toolValue = (plytixIssue || ucIssue)?.tool ?? null;
                        return (
                            <TableRow key={r.bundleSku} hover>
                                <TableCell>
                                    <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '0.84rem', lineHeight: 1.25 }}>{r.bundleSku}</Typography>
                                    {r.parentSKU !== r.bundleSku && <Typography sx={{ fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: 600, color: '#6B625B' }}>{r.parentSKU}</Typography>}
                                </TableCell>
                                <TableCell><Chip size="small" variant="outlined" color={r.saleType === 'Simple' ? 'primary' : 'default'} label={r.saleType || 'None'} sx={{ height: 20, fontSize: '0.66rem', fontWeight: 600 }} /></TableCell>
                                <TableCell><ValueChip value={toolValue} color="#B8451E" onCopy={onCopy} /></TableCell>
                                {showPlytix && <TableCell>{plytixIssue ? <ValueChip value={plytixIssue.other} color={SYSTEM_COLORS.plytix} onCopy={onCopy} /> : <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled' }}>—</Typography>}</TableCell>}
                                {showUC && <TableCell>{ucIssue ? <ValueChip value={ucIssue.other} color={SYSTEM_COLORS.unicommerce} onCopy={onCopy} /> : <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled' }}>—</Typography>}</TableCell>}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

/* ── Compact grouped issue chip — used only in the "All checks" search/lookup view ── */
const IssueSummaryChip: React.FC<{ label: string; severity: DataHealthSeverity; count?: number; flagged?: boolean }> = ({ label, severity, count, flagged }) => (
    <Chip
        size="small"
        icon={flagged ? <Flag sx={{ fontSize: '13px !important' }} /> : undefined}
        label={count && count > 1 ? `${label} (${count})` : label}
        sx={{
            height: 22, fontSize: '0.68rem', fontWeight: flagged ? 700 : 600,
            bgcolor: flagged ? '#dc2626' : severity === 'error' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(245, 158, 11, 0.12)',
            color: flagged ? '#fff' : severity === 'error' ? '#dc2626' : '#b45309',
            border: '1px solid',
            borderColor: flagged ? '#dc2626' : severity === 'error' ? 'rgba(239, 68, 68, 0.25)' : 'rgba(245, 158, 11, 0.3)',
            '& .MuiChip-icon': { color: flagged ? '#fff' : undefined, ml: 0.6 },
        }}
    />
);

/* ── Accordion row — kept only for the "All checks" search/lookup fallback,
     where results are naturally few (a specific SKU search, or the 7 highlighted). ── */
const IssueRow: React.FC<{ result: DataHealthResult; onCopy: () => void }> = ({ result, onCopy }) => {
    const [expanded, setExpanded] = useState(false);
    const nameCollision = result.issues.find(i => i.type === 'name_equals_parent_sku');
    const saleTypeEmpty = result.issues.find(i => i.type === 'sale_type_empty');
    const noPrimary = result.issues.find(i => i.type === 'no_primary_variant');
    const plytixPriceMissing = result.issues.filter(i => i.type === 'plytix_price_missing');
    const ucBundleManual = result.issues.find(i => i.type === 'uc_bundle_price_manual');
    const missingPlytix = result.issues.find(i => i.type === 'missing_in_plytix');
    const missingUC = result.issues.find(i => i.type === 'missing_in_unicommerce');
    const grouped = useMemo(() => groupByField(result.issues), [result.issues]);

    const summaryChips: { key: string; label: string; severity: DataHealthSeverity; count?: number; flagged?: boolean }[] = [];
    if (nameCollision) summaryChips.push({ key: 'nc', label: 'Name = Parent SKU', severity: 'error', flagged: true });
    if (missingPlytix) summaryChips.push({ key: 'mp', label: 'Missing in Plytix', severity: 'error' });
    if (missingUC) summaryChips.push({ key: 'muc', label: 'Missing in Unicommerce', severity: 'error' });
    if (saleTypeEmpty) summaryChips.push({ key: 'ste', label: 'Sale Type Missing', severity: 'error' });
    if (noPrimary) summaryChips.push({ key: 'npv', label: 'No Primary Variant', severity: 'warning' });
    if (ucBundleManual) summaryChips.push({ key: 'ubm', label: 'UC Bundle Price — Set Manually', severity: 'warning', flagged: true });
    if (plytixPriceMissing.length > 0) summaryChips.push({ key: 'ppm', label: 'Price Missing in Plytix', severity: 'error', count: plytixPriceMissing.length });
    grouped.forEach(g => summaryChips.push({ key: g.field, label: FIELD_LABELS[g.field] || g.field, severity: g.severity, count: g.items.length }));

    return (
        <>
            <TableRow
                hover
                onClick={() => setExpanded(e => !e)}
                sx={{
                    cursor: 'pointer',
                    '& td': { borderBottom: expanded ? 'none' : undefined },
                    ...(result.highlighted ? { '& td:first-of-type': { borderLeft: '3px solid #dc2626' } } : result.ucBundleManual ? { '& td:first-of-type': { borderLeft: '3px solid #d97706' } } : {}),
                }}
            >
                <TableCell sx={{ width: 36 }}>
                    <motion.div animate={{ rotate: expanded ? 180 : 0 }} transition={{ duration: 0.18 }} style={{ display: 'flex' }}>
                        <ExpandMore fontSize="small" sx={{ color: 'text.secondary' }} />
                    </motion.div>
                </TableCell>
                <TableCell sx={{ minWidth: 150 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {result.highlighted && <Flag sx={{ fontSize: 14, color: '#dc2626' }} />}
                        <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '0.84rem', lineHeight: 1.25 }}>{result.bundleSku}</Typography>
                    </Box>
                    {result.parentSKU !== result.bundleSku && (
                        <Typography sx={{ fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: 600, color: '#6B625B' }}>{result.parentSKU}</Typography>
                    )}
                </TableCell>
                <TableCell sx={{ width: 90 }}>
                    <Chip size="small" variant="outlined" color={result.saleType === 'Simple' ? 'primary' : 'default'} label={result.saleType || 'None'} sx={{ height: 20, fontSize: '0.66rem', fontWeight: 600 }} />
                </TableCell>
                <TableCell sx={{ width: 110 }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {result.errorCount > 0 && (
                            <Chip size="small" icon={<ErrorOutline sx={{ fontSize: '13px !important' }} />} label={result.errorCount} sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#dc2626', color: '#fff', '& .MuiChip-icon': { ml: 0.5, color: '#fff' } }} />
                        )}
                        {result.warningCount > 0 && (
                            <Chip size="small" icon={<WarningAmber sx={{ fontSize: '13px !important' }} />} label={result.warningCount} sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: 'rgba(245, 158, 11, 0.15)', color: '#b45309', '& .MuiChip-icon': { ml: 0.5 } }} />
                        )}
                    </Box>
                </TableCell>
                <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                        {summaryChips.map(c => <IssueSummaryChip key={c.key} label={c.label} severity={c.severity} count={c.count} flagged={c.flagged} />)}
                    </Box>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
                    <AnimatePresence initial={false}>
                        {expanded && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                                style={{ overflow: 'hidden' }}
                            >
                                <Box sx={{ px: 3, py: 2, bgcolor: '#FAFAF7', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {nameCollision && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderRadius: '10px', bgcolor: 'rgba(220, 38, 38, 0.08)', border: '1.5px solid rgba(220, 38, 38, 0.35)' }}>
                                            <Flag sx={{ fontSize: 18, color: '#dc2626', flexShrink: 0 }} />
                                            <Typography sx={{ fontSize: '0.8rem', color: '#7f1d1d' }}>
                                                The bundle's <strong>Name</strong> is set to <strong>"{nameCollision.tool}"</strong> — identical to its Parent SKU. This should never happen; give it a real product name.
                                            </Typography>
                                        </Box>
                                    )}
                                    {missingPlytix && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderRadius: '10px', bgcolor: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                                            <ErrorOutline sx={{ fontSize: 18, color: '#dc2626', flexShrink: 0 }} />
                                            <Typography sx={{ fontSize: '0.8rem', color: '#7f1d1d' }}>
                                                <strong>{result.bundleSku}</strong> could not be found in Plytix — verify it still exists there, or that the SKU matches exactly.
                                            </Typography>
                                        </Box>
                                    )}
                                    {missingUC && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderRadius: '10px', bgcolor: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                                            <ErrorOutline sx={{ fontSize: 18, color: '#dc2626', flexShrink: 0 }} />
                                            <Typography sx={{ fontSize: '0.8rem', color: '#7f1d1d' }}>
                                                <strong>{result.bundleSku}</strong> has never been synced to Unicommerce.
                                            </Typography>
                                        </Box>
                                    )}
                                    {saleTypeEmpty && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderRadius: '10px', bgcolor: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                                            <ErrorOutline sx={{ fontSize: 18, color: '#dc2626', flexShrink: 0 }} />
                                            <Typography sx={{ fontSize: '0.8rem', color: '#7f1d1d' }}>
                                                <strong>Sale Type</strong> is empty in the tool, but <strong>{result.bundleSku}</strong> is a real, synced item in Unicommerce — it should be marked Simple or Bundle.
                                            </Typography>
                                        </Box>
                                    )}
                                    {ucBundleManual && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderRadius: '10px', bgcolor: 'rgba(245, 158, 11, 0.1)', border: '1.5px solid rgba(217, 119, 6, 0.5)' }}>
                                            <Flag sx={{ fontSize: 18, color: '#b45309', flexShrink: 0 }} />
                                            <Typography sx={{ fontSize: '0.8rem', color: '#78350f' }}>
                                                <strong>{result.bundleSku}</strong> is a bundle in Unicommerce, whose API ignores price edits. Tool: <strong>{ucBundleManual.tool}</strong> · Unicommerce: <strong>{ucBundleManual.other}</strong>. Set the price in Unicommerce.
                                            </Typography>
                                        </Box>
                                    )}
                                    {plytixPriceMissing.length > 0 && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderRadius: '10px', bgcolor: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                                            <ErrorOutline sx={{ fontSize: 18, color: '#dc2626', flexShrink: 0 }} />
                                            <Typography sx={{ fontSize: '0.8rem', color: '#7f1d1d' }}>
                                                <strong>{plytixPriceMissing.map(i => (i.field === 'maxRetailPrice' ? 'MRP' : 'Base Price')).join(' and ')}</strong> {plytixPriceMissing.length > 1 ? 'are' : 'is'} empty in Plytix for <strong>{result.bundleSku}</strong> ({plytixPriceMissing.map(i => `${i.field === 'maxRetailPrice' ? 'MRP' : 'Base'} in tool: ${i.tool ?? 'empty'}`).join(', ')}). Fill it in Plytix.
                                            </Typography>
                                        </Box>
                                    )}
                                    {noPrimary && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderRadius: '10px', bgcolor: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.25)' }}>
                                            <WarningAmber sx={{ fontSize: 18, color: '#b45309', flexShrink: 0 }} />
                                            <Typography sx={{ fontSize: '0.8rem', color: '#78350f' }}>
                                                <strong>{result.parentSKU}</strong> is live, but none of its versions ({noPrimary.other}) is marked Primary. Mark one in the parent's version list.
                                            </Typography>
                                        </Box>
                                    )}

                                    {grouped.map((g, idx) => (
                                        <motion.div
                                            key={g.field}
                                            initial={{ opacity: 0, x: -6 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.18, delay: idx * 0.03 }}
                                        >
                                            <Box sx={{ borderRadius: '10px', border: '1px solid #F3F2EC', bgcolor: '#fff', overflow: 'hidden' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.9, bgcolor: g.severity === 'error' ? 'rgba(239, 68, 68, 0.05)' : '#FAFAF7', borderBottom: '1px solid #F3F2EC' }}>
                                                    {g.severity === 'error' ? <ErrorOutline sx={{ fontSize: 15, color: '#dc2626' }} /> : <WarningAmber sx={{ fontSize: 15, color: '#f59e0b' }} />}
                                                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1A1410' }}>{FIELD_LABELS[g.field] || g.field}</Typography>
                                                    <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>· in tool:</Typography>
                                                    <ValueChip value={g.toolValue} color="#B8451E" onCopy={onCopy} />
                                                </Box>
                                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                    {g.items.map((issue, i) => (
                                                        <Box
                                                            key={issue.system}
                                                            sx={{
                                                                display: 'grid',
                                                                gridTemplateColumns: '120px 1fr',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                px: 1.5,
                                                                py: 0.85,
                                                                borderTop: i > 0 ? '1px solid #F3F2EC' : 'none',
                                                            }}
                                                        >
                                                            <Chip
                                                                size="small"
                                                                label={SYSTEM_LABELS[issue.system]}
                                                                sx={{ height: 20, fontSize: '0.66rem', fontWeight: 700, width: 'fit-content', bgcolor: `${SYSTEM_COLORS[issue.system]}1a`, color: SYSTEM_COLORS[issue.system] }}
                                                            />
                                                            <ValueChip value={issue.other} color={SYSTEM_COLORS[issue.system]} onCopy={onCopy} />
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        </motion.div>
                                    ))}
                                </Box>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </TableCell>
            </TableRow>
        </>
    );
};

const DataHealthTab: React.FC = () => {
    const [report, setReport] = useState<DataHealthReport | null>(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [checkFilter, setCheckFilter] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const toast = useToast();
    const [syncing, setSyncing] = useState(false);

    const [lastSynced, setLastSynced] = useState<{ count: number; at: Date } | null>(null);
    const pageSize = 30;

    const runCheck = useCallback(async () => {
        setLoading(true);
        try {
            const result = await WarehouseAPI.getDataHealthCheck();
            setReport(result);
            setPage(1);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { runCheck(); }, [runCheck]);

    const handleSync = async () => {
        setSyncing(true);
        try {
            const result = await WarehouseAPI.syncUnicommerceProducts();
            setLastSynced({ count: result.uniqueSkus, at: new Date() });
            await runCheck();
        } catch (err: any) {
            toast.error(
                err?.response?.status === 403
                    ? "You don't have permission to sync Unicommerce data — admin or warehouse-manager role required."
                    : (err?.response?.data?.message || 'Failed to sync Unicommerce data.'),
                { duration: 5000 },
            );
        } finally {
            setSyncing(false);
        }
    };

    // The Issue Breakdown — one card per distinct check, sorted worst-first.
    // This is the primary navigation now: pick a problem type before seeing any SKUs.
    const checkSummary = useMemo(() => {
        if (!report) return [];
        const map = new Map<string, { key: string; total: number; plytix: number; unicommerce: number; severity: DataHealthSeverity }>();
        report.results.forEach(r => {
            const seenKeys = new Set<string>();
            r.issues.forEach(i => {
                const key = checkKeyOf(i);
                if (!map.has(key)) map.set(key, { key, total: 0, plytix: 0, unicommerce: 0, severity: 'warning' });
                const m = map.get(key)!;
                if (!seenKeys.has(key)) { m.total++; seenKeys.add(key); }
                if (i.system === 'plytix') m.plytix++;
                if (i.system === 'unicommerce') m.unicommerce++;
                if (i.severity === 'error') m.severity = 'error';
            });
        });
        return Array.from(map.values()).sort((a, b) => b.total - a.total);
    }, [report]);

    const searching = search.trim().length > 0;

    const filtered = useMemo(() => {
        if (!report) return [];
        const q = search.trim().toUpperCase();
        return report.results.filter(r => {
            if (q && !r.bundleSku.includes(q) && !r.parentSKU.includes(q)) return false;
            if (checkFilter && !searching && !r.issues.some(i => checkKeyOf(i) === checkFilter)) return false;
            return true;
        });
    }, [report, search, checkFilter, searching]);

    const paged = filtered.slice((page - 1) * pageSize, page * pageSize);
    const resetFilters = () => { setSearch(''); setCheckFilter(null); setPage(1); };

    // A lens is "active" once a check is picked or a search is typed — that's when
    // we actually render SKU rows. Otherwise show the breakdown grid and stop there.
    const hasLens = Boolean(checkFilter || searching);

    return (
        <Box sx={{ pb: 4 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>Data Health</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Every active bundle, checked against Plytix (source of truth) and Unicommerce.
                        {report && ` Last checked ${new Date(report.checkedAt).toLocaleString()}.`}
                        {lastSynced && ` Unicommerce catalogue last synced ${lastSynced.at.toLocaleTimeString()} (${lastSynced.count.toLocaleString()} SKUs).`}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Pull the latest Unicommerce catalogue, then recheck — Recheck alone reuses whatever was synced last">
                        <span>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={syncing ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <CloudSync />}
                                onClick={handleSync}
                                disabled={syncing || loading}
                                sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#DC5829', '&:hover': { bgcolor: '#B8451E' } }}
                            >
                                {syncing ? 'Syncing Unicommerce…' : 'Sync & Recheck'}
                            </Button>
                        </span>
                    </Tooltip>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={loading && !syncing ? <CircularProgress size={14} /> : <Refresh />}
                        onClick={runCheck}
                        disabled={loading || syncing}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        {loading && !syncing ? 'Checking…' : 'Recheck'}
                    </Button>
                </Box>
            </Box>

            {loading && !report ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 8 }}>
                    <CircularProgress sx={{ color: '#DC5829' }} />
                    <Typography variant="body2" color="text.secondary">Comparing bundles against Plytix and Unicommerce…</Typography>
                </Box>
            ) : report ? (
                <>
                    {/* Highlighted callout — Parent SKU = Name is the #1 check, always shown
                        separately and prominently when it finds anything, regardless of
                        where it'd otherwise rank in the breakdown grid by count alone. */}
                    {report.totalHighlighted > 0 && (
                        <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
                            <Box
                                onClick={() => { setCheckFilter(f => f === 'name_equals_parent_sku' ? null : 'name_equals_parent_sku'); setSearch(''); setPage(1); }}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, mb: 2,
                                    borderRadius: '12px', cursor: 'pointer',
                                    bgcolor: checkFilter === 'name_equals_parent_sku' ? '#dc2626' : 'rgba(220, 38, 38, 0.06)',
                                    border: '1.5px solid #dc2626',
                                    transition: 'background-color 150ms ease',
                                }}
                            >
                                <Flag sx={{ color: checkFilter === 'name_equals_parent_sku' ? '#fff' : '#dc2626', fontSize: 22 }} />
                                <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: checkFilter === 'name_equals_parent_sku' ? '#fff' : '#7f1d1d' }}>
                                    {report.totalHighlighted} bundle{report.totalHighlighted === 1 ? '' : 's'} {report.totalHighlighted === 1 ? 'has' : 'have'} its Name set identical to its Parent SKU — this should never happen.
                                </Typography>
                                <Box sx={{ flex: 1 }} />
                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: checkFilter === 'name_equals_parent_sku' ? '#fff' : '#dc2626', textDecoration: 'underline' }}>
                                    {checkFilter === 'name_equals_parent_sku' ? 'Showing only these' : 'Show only these'}
                                </Typography>
                            </Box>
                        </motion.div>
                    )}

                    {(report.totalUcBundleManual ?? 0) > 0 && (
                        <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.05 }}>
                            <Box
                                onClick={() => { setCheckFilter(f => f === 'uc_bundle_price_manual' ? null : 'uc_bundle_price_manual'); setSearch(''); setPage(1); }}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, mb: 2,
                                    borderRadius: '12px', cursor: 'pointer',
                                    bgcolor: checkFilter === 'uc_bundle_price_manual' ? '#d97706' : 'rgba(245, 158, 11, 0.1)',
                                    border: '1.5px solid #d97706',
                                    transition: 'background-color 150ms ease',
                                }}
                            >
                                <Flag sx={{ color: checkFilter === 'uc_bundle_price_manual' ? '#fff' : '#b45309', fontSize: 22 }} />
                                <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: checkFilter === 'uc_bundle_price_manual' ? '#fff' : '#78350f' }}>
                                    {report.totalUcBundleManual} bundle{report.totalUcBundleManual === 1 ? '' : 's'} {report.totalUcBundleManual === 1 ? 'has' : 'have'} a price that differs in Unicommerce and can't be updated through its API — set {report.totalUcBundleManual === 1 ? 'it' : 'them'} in Unicommerce
                                </Typography>
                                <Box sx={{ flex: 1 }} />
                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: checkFilter === 'uc_bundle_price_manual' ? '#fff' : '#b45309', textDecoration: 'underline' }}>
                                    {checkFilter === 'uc_bundle_price_manual' ? 'Showing only these' : 'Show only these'}
                                </Typography>
                            </Box>
                        </motion.div>
                    )}

                    {/* Stat tiles */}
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2.5 }}>
                        <StatTile
                            index={0}
                            icon={<Inventory2 sx={{ fontSize: 18 }} />}
                            label="Active Bundles Checked"
                            value={report.totalChecked}
                            color="#6B625B"
                            bg="rgba(100, 116, 139, 0.08)"
                            active={!hasLens}
                            onClick={resetFilters}
                        />
                        <StatTile
                            index={1}
                            icon={<CheckCircle sx={{ fontSize: 18 }} />}
                            label="In Sync"
                            value={report.totalChecked - report.totalWithIssues}
                            color="#16a34a"
                            bg="rgba(34, 197, 94, 0.1)"
                            active={false}
                            onClick={resetFilters}
                        />
                        <StatTile
                            index={2}
                            icon={<ErrorOutline sx={{ fontSize: 18 }} />}
                            label="Errors (missing / empty)"
                            value={report.totalErrors}
                            color="#dc2626"
                            bg="rgba(239, 68, 68, 0.1)"
                            active={false}
                            onClick={resetFilters}
                        />
                        <StatTile
                            index={3}
                            icon={<WarningAmber sx={{ fontSize: 18 }} />}
                            label="Warnings (differs)"
                            value={report.totalWarnings}
                            color="#b45309"
                            bg="rgba(245, 158, 11, 0.12)"
                            active={false}
                            onClick={resetFilters}
                        />
                    </Box>

                    {report.totalWithIssues === 0 ? (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 8, border: '1px dashed #DAD6CC', borderRadius: '14px' }}>
                                <CheckCircle sx={{ fontSize: 40, color: '#16a34a' }} />
                                <Typography sx={{ fontWeight: 700 }}>Everything's in sync</Typography>
                                <Typography variant="body2" color="text.secondary">All {report.totalChecked} active bundles match Plytix and Unicommerce.</Typography>
                            </Box>
                        </motion.div>
                    ) : (
                        <>
                            {/* Issue Breakdown — the primary navigation */}
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
                                Issue Breakdown — pick one to see affected SKUs
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', mb: 2.5 }}>
                                {checkSummary.map((c, idx) => (
                                    <CheckCard
                                        key={c.key}
                                        checkKey={c.key}
                                        total={c.total}
                                        plytixCount={c.plytix}
                                        ucCount={c.unicommerce}
                                        severity={c.severity}
                                        active={checkFilter === c.key}
                                        onClick={() => { setCheckFilter(f => f === c.key ? null : c.key); setSearch(''); setPage(1); }}
                                        index={idx}
                                    />
                                ))}
                            </Box>

                            {/* Search always works, independent of a check being picked */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                <TextField
                                    size="small"
                                    placeholder="Or search a specific SKU…"
                                    value={search}
                                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18 }} /></InputAdornment> }}
                                    sx={{ minWidth: 240, bgcolor: '#fff' }}
                                />
                                {checkFilter && (
                                    <Chip
                                        label={`${CHECK_META[checkFilter]?.label || checkFilter} — ${filtered.length} SKUs`}
                                        onDelete={() => setCheckFilter(null)}
                                        deleteIcon={<Close sx={{ fontSize: 16 }} />}
                                        sx={{ fontWeight: 600, bgcolor: '#1A1410', color: '#fff' }}
                                    />
                                )}
                                {hasLens && (
                                    <Button size="small" startIcon={<ArrowBack sx={{ fontSize: 14 }} />} onClick={resetFilters} sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.75rem' }}>
                                        Back to breakdown
                                    </Button>
                                )}
                                <Box sx={{ flex: 1 }} />
                                {hasLens && <Typography variant="caption" color="text.secondary">{filtered.length} matching</Typography>}
                            </Box>

                            <AnimatePresence mode="wait">
                                {!hasLens ? (
                                    <motion.div key="prompt" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 6, border: '1px dashed #DAD6CC', borderRadius: '12px' }}>
                                            <Typography variant="body2" color="text.secondary">Pick a check above, or search a SKU, to see the affected bundles.</Typography>
                                        </Box>
                                    </motion.div>
                                ) : filtered.length === 0 ? (
                                    <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 6, border: '1px dashed #DAD6CC', borderRadius: '12px' }}>
                                            <ImageNotSupported sx={{ fontSize: 28, color: 'text.disabled' }} />
                                            <Typography variant="body2" color="text.secondary">No results match</Typography>
                                        </Box>
                                    </motion.div>
                                ) : (
                                    <motion.div key={checkFilter || 'search'} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.18 }}>
                                        {checkFilter ? (
                                            <FlatCheckTable checkKey={checkFilter} results={paged} onCopy={() => toast('Copied to clipboard', { duration: 1500 })} />
                                        ) : (
                                            <TableContainer component={Paper} variant="outlined" sx={tableContainerSx}>
                                                <Table size="small" sx={tableBodySx}>
                                                    <TableHead>
                                                        <TableRow sx={tableHeadRowSx}>
                                                            <TableCell sx={{ width: 36 }} />
                                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem' }}>SKU</TableCell>
                                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem' }}>TYPE</TableCell>
                                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem' }}>SEVERITY</TableCell>
                                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem' }}>ISSUES</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {paged.map((result) => (
                                                            <IssueRow key={result.bundleSku} result={result} onCopy={() => toast('Copied to clipboard', { duration: 1500 })} />
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )}
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {hasLens && filtered.length > pageSize && (
                                <TablePaginationBar page={page - 1} total={filtered.length} rowsPerPage={pageSize} onPageChange={(p) => setPage(p + 1)} />
                            )}
                        </>
                    )}
                </>
            ) : null}

        </Box>
    );
};

export default DataHealthTab;
